import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { FieldExpression, RangeExpression, toString as luceneToString } from 'lucene';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { DocumentService } from '../../services/documents.service';
import { DatacenterDefaultConfiguration } from '../../../shared/datacenter/datacenter-default-configuration';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { Document } from '../../models/document';
import { TranslationService } from '../../../shared/services/translation.service';
import { getDefaultDatacenterViews } from '../../../shared/datacenter/default-datacenter-views';

const SERVICE_CODE = 'PDOCS';

type DocumentCountResult = Pick<PaginatedResult<Document>, 'count'>;

@Component({
	selector: 'copo-documents-last-connection-widget',
	templateUrl: './documents-last-connection-widget.component.html',
	styleUrl: './documents-last-connection-widget.component.css',
	imports: [NgIf, TranslateModule, MatButtonModule, MatIconModule, LoadingSpinnerComponent]
})
export class DocumentsLastConnectionWidgetComponent implements OnDestroy {
	user: User;
	isLoading = true;
	hasError = false;
	oaCount?: number;
	daCount?: number;
	ceCount?: number;
	inCount?: number;
	xcarbCount?: number;

	readonly showOa: boolean;
	readonly showDa: boolean;
	readonly showCe: boolean;
	readonly showIn: boolean;
	readonly showXcarb: boolean;

	private readonly countSub?: Subscription;

	constructor(
		private readonly userService: UserService,
		private readonly documentService: DocumentService,
		private readonly router: Router,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly translationService: TranslationService
	) {
		this.user = this.userService.user;
		this.showOa = this.user.hasServiceParameterEqualsTo(SERVICE_CODE, 'OrderAcknowledgment', 'True');
		this.showDa = this.user.hasServiceParameterEqualsTo(SERVICE_CODE, 'DispatchAdvice', 'True');
		this.showCe = this.user.hasServiceParameterEqualsTo(SERVICE_CODE, 'Certificate', 'True');
		this.showIn = this.user.hasServiceParameterEqualsTo(SERVICE_CODE, 'Invoice', 'True');
		this.showXcarb = this.user.hasServiceParameterEqualsTo(SERVICE_CODE, 'XCarb', 'True');

		const requests: {
			oa?: Observable<DocumentCountResult>;
			da?: Observable<DocumentCountResult>;
			ce?: Observable<DocumentCountResult>;
			in?: Observable<DocumentCountResult>;
			xcarb?: Observable<DocumentCountResult>;
		} = {};

		if (this.showOa) {
			requests.oa = this.countDocuments('OA');
		}
		if (this.showDa) {
			requests.da = this.countDocuments('DA');
		}
		if (this.showCe) {
			requests.ce = this.countDocuments('CE');
		}
		if (this.showIn) {
			requests.in = this.countDocuments('IN');
		}
		if (this.showXcarb) {
			requests.xcarb = this.countDocuments('XCARB');
		}

		if (Object.keys(requests).length === 0) {
			this.isLoading = false;
			return;
		}

		this.countSub = forkJoin(requests).subscribe({
			next: results => {
				if (this.showOa) {
					this.oaCount = results.oa?.count;
				}
				if (this.showDa) {
					this.daCount = results.da?.count;
				}
				if (this.showCe) {
					this.ceCount = results.ce?.count;
				}
				if (this.showIn) {
					this.inCount = results.in?.count;
				}
				if (this.showXcarb) {
					this.xcarbCount = results.xcarb?.count;
				}
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error('Error loading document counts since last connection', err);
				this.isLoading = false;
				this.hasError = true;
			}
		});
	}

	async goToDatacenter(documentType: string) {
		// Map document type code to view for document-type-specific column configuration
		const typeViewMap: Record<string, -2 | -3 | -4 | -5 | -6> = {
			IN: -2, // Invoice
			DA: -3, // Dispatch Advice
			OA: -4, // Order Acknowledgment
			CE: -5, // Certificate
			XCARB: -6 // X-CARB
		};

		// Get translations for the document types
		const translations = {
			invoice: this.translationService.getTR('commercial-documents.invoice'),
			dispatchAdvice: this.translationService.getTR('datacenter-filter.dispatch-advice'),
			orderAcknowledgment: this.translationService.getTR('order.card.document.order.acknowledgments'),
			certificate: this.translationService.getTR('order.card.document.certificates'),
			xcarb: this.translationService.getTR('order.card.xcarb')
		};

		// Get all available document type views
		const availableViews = getDefaultDatacenterViews(this.user, 'document', translations);

		// Find the view for this document type
		const viewId = typeViewMap[documentType];
		let defaultView = availableViews.find(v => v.id === viewId);

		// If no specific view found, fall back to generic document view
		if (!defaultView) {
			defaultView = this.defaultConfiguration.getDefaultView('document');
		}

		// Create a copy to avoid modifying the original
		defaultView = { ...defaultView };

		// Ensure redirection pre-fills only the FROM value for "Received by SteelUser".
		const existingFilters = (defaultView.filterState || []).filter(
			filter => filter.id !== 'receivedBySteeluser' && filter.id !== 'receiptDocumentDate'
		);
		defaultView.filterState = [
			...existingFilters,
			{
				id: 'receivedBySteeluser',
				values: [
					{
						from: new Date(this.user.previousLastConnectionDate),
						to: '',
						entityName: ''
					}
				]
			}
		];

		await this.router.navigate(['/commercial-documents/datacenter'], {
			state: { view: defaultView }
		});
	}

	ngOnDestroy() {
		this.countSub?.unsubscribe();
	}

	private countDocuments(documentType: string): Observable<DocumentCountResult> {
		const typeFilter: FieldExpression = {
			field: 'type',
			term: documentType,
			quoted: true
		};
		const dateFilter: RangeExpression = {
			field: 'receiptDocumentDate',
			term_min: this.user.previousLastConnectionDate,
			term_max: '*',
			inclusive: 'none'
		};
		const filter = luceneToString({
			left: typeFilter,
			operator: 'AND',
			right: dateFilter,
			parenthesized: true
		});
		return this.documentService.getDocumentsCount(filter);
	}
}
